import { createSelector } from 'reselect'
import { EmptyArray } from 'lib/array/arrayUtils'
import { canUseAirportTransferBenefit, hasIssuedLoyaltyBenefit } from 'loyalty/lib/airportTransferBenefitUtils'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getOrderCanRedeemTransferLoyaltyBenefit from 'checkout/selectors/loyalty/getOrderCanRedeemTransferLoyaltyBenefit'
import { isOrderInternationalTrip } from 'checkout/selectors/loyalty/getOrderIsLoyaltyInternationalTrip'
import determineOrderLoyaltyTransferExperience from 'checkout/selectors/loyalty/determineOrderLoyaltyTransferExperience'
import determineLoyaltyEligibleTransferOptions from 'checkout/selectors/loyalty/determineLoyaltyEligibleTransferOptions'

/** Apply the order-page rules to the homepage booking, independently of the current route. */
const getUpcomingEscapeTransferEligibility = createSelector(
  (_state: App.State, order: App.Order) => order,
  (state: App.State) => state.loyalty.account.data,
  (state: App.State) => state.loyalty.config,
  getIsCurrentRegionUserLoyaltyRegion,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => state.cruise.cruiseOfferSnapshot,
  (state: App.State) => state.experience.experienceLists,
  (state: App.State) => state.experience.experiences,
  (state: App.State) => state.experience.experienceTransferOptions,
  (order, account, config, isLoyaltyRegion, flights, cruises, lists, experiences, options) => {
    const transferExperience = determineOrderLoyaltyTransferExperience(order, lists, experiences)
    const transferOptions = determineLoyaltyEligibleTransferOptions(order, transferExperience, options)

    return getOrderCanRedeemTransferLoyaltyBenefit.resultFunc(
      order,
      account?.benefits,
      account?.tier,
      config,
      isOrderInternationalTrip(order, flights, cruises),
      isLoyaltyRegion,
      config?.transferAirports ?? EmptyArray,
      transferOptions,
    )
  },
)

/** Do not request itineraries for cards without a usable entitlement or known international trips. */
export const getUpcomingEscapeNeedsTransferItinerary = createSelector(
  (_state: App.State, order: App.Order) => order,
  (state: App.State) => state.loyalty.account.data,
  (state: App.State) => state.loyalty.config,
  getIsCurrentRegionUserLoyaltyRegion,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => state.cruise.cruiseOfferSnapshot,
  (order, account, config, isLoyaltyRegion, flights, cruises) => {
    return order.isBookingOwner &&
      !!order.loyaltyDetails.tier &&
      isLoyaltyRegion &&
      !!config?.transferAirports.length &&
      !hasIssuedLoyaltyBenefit(order, 'airport_transfer') &&
      (order.flightItems.length > 0 || order.cruiseItems.length > 0) &&
      canUseAirportTransferBenefit({
        userBenefits: account?.benefits,
        loyaltyConfig: config,
        accountTier: account?.tier,
      }) &&
      !isOrderInternationalTrip(order, flights, cruises)
  },
)

export default getUpcomingEscapeTransferEligibility
